import { Controller, useFormContext } from 'react-hook-form';

import { Checkbox } from '@/components/core/Checkbox';
import { Loader } from '@/components/core/Loader';
import { RadioGroup, RadioGroupItem } from '@/components/core/RadioGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/core/Select';
import { logger } from '@/services/logger';
import { cn } from '@/services/utils';
import { DataModelAttributeRenderer } from '@/types/DataModel';

import { useFlexWrap } from '../hooks/useFlexWrap';
import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useOptions } from '../hooks/useOptions';
import { useOptionTranslator } from '../hooks/useOptionTranslator';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';

interface BooleanComponentProps extends EditorComponent {
    className?: string;
}

const BooleanComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    gridMode = false,
    hideLabel,
    readonly= false,
    hidden = false,
    isKeyAttribute,
    beforeAttributeControl,
    className,
    labelClassName,
    handleUrlChange
}: BooleanComponentProps) => {

    const focusButtonRef = useFocusOnAttribute<HTMLButtonElement>(attribute);
    const focusDivRef = useFocusOnAttribute<HTMLDivElement>(attribute);

    const { translateOption } = useOptionTranslator();
    const { loading: loadingOptions, options } = useOptions({ attribute, item: attribute.item });
    const { control } = useFormContext();
    const { containerRef, isWrapping } = useFlexWrap();

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    if (hidden) {
        return null;
    }

    if (attribute.params?.values?.length === 0) {
        logger.error(`BooleanComponent: Attribute ${attribute.name} of type Boolean has no values.`);
        return null;
    }

    if (loadingOptions) {
        return (
            <Layout attribute={attribute} hideLabel={hideLabel} direction={direction} fieldPath={fieldPath} gridMode={gridMode} labelClassName={labelClassName}>
                <Loader />
            </Layout>
        );
    }

    if (attribute.params?.renderer === DataModelAttributeRenderer.SingleBoolean) {

        if (gridMode) {
            // Case: Single choice boolean in a grid
            return (
                <Layout
                    attribute={attribute}
                    className="tw-text-center"
                    direction={direction}
                    hideLabel={hideLabel}
                    fieldPath={fieldPath}
                    gridMode={gridMode}
                    isKeyAttribute={isKeyAttribute}
                    beforeAttributeControl={beforeAttributeControl}
                    isClearable={!gridMode && !readonly}>
                    <div className="tw-flex tw-items-center tw-justify-center" style={{ minHeight: '36px' }}>
                        <Controller
                            control={control}
                            name={fieldPath}
                            render={({ field }) =>
                                <Checkbox
                                    id={fieldPath}
                                    disabled={readonly}
                                    ref={focusButtonRef}
                                    isChecked={field.value === options[0].key}
                                    onCheckedChange={(checked) => {
                                        const value = checked ? options[0].key : null;
                                        field.onChange(value);
                                    }}
                                />
                            }
                        />
                    </div>
                </Layout>
            );

        } else {
            // Case: Single choice boolean
            return (
                <Layout
                    attribute={attribute}
                    hideLabel={hideLabel}
                    direction={direction}
                    fieldPath={fieldPath}
                    gridMode={gridMode}
                    isClearable={!readonly}
                >
                    <div className="tw-flex tw-items-center" style={{ minHeight: '36px' }}>
                        <Controller
                            control={control}
                            name={fieldPath}
                            render={({ field }) => (
                                <Checkbox
                                    id={fieldPath}
                                    disabled={readonly}
                                    ref={focusButtonRef}
                                    onClick={handleUrlChange}
                                    isChecked={field.value === options[0].key}
                                    onCheckedChange={(checked) => {
                                        const value = checked ? options[0].key : null;
                                        field.onChange(value);
                                    }}
                                />
                            )}
                        />
                        <label
                            htmlFor={fieldPath}
                            className={cn(`tw-m-0 tw-ml-2 lg-font-dynamic tw-leading-none ${
                                readonly ? '!tw-text-t-inactive' : 'tw-text-t-primary'
                            }`)}
                        >
                            {translateOption(options[0].key, attribute)}
                        </label>
                    </div>
                </Layout>
            );

        }
    } else {

        if (gridMode) {
            // Case: Multi choice boolean in a grid
            return (
                <Controller
                    control={control}
                    name={fieldPath}
                    render={({ field }) =>
                        <Select value={field.value || ''} onValueChange={(value) => {
                            field.onChange(value);
                        }}>
                            <SelectTrigger borderless={gridMode} className={className}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent ref={focusDivRef}>
                                {options.map(option => <SelectItem key={option.key} value={option.key}>{translateOption(option.key, attribute)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    }
                />
            );

        } else {
            // Case: Default multi choice boolean
            // isWrapping indicates if the flex container is wrapping on small screens
            // You can use it for conditional styling or logic, e.g.:
            // className={`tw-flex tw-flex-wrap tw-gap-y-4 ${isWrapping ? 'some-additional-class' : ''}`}

            return (
                <Layout attribute={attribute} hideLabel={hideLabel} direction={direction} fieldPath={fieldPath} gridMode={gridMode} labelVerticalAlign={isWrapping ? 'top' : undefined}>
                    <Controller
                        control={control}
                        name={fieldPath}
                        render={({ field }) =>
                            <RadioGroup
                                ref={containerRef}
                                className="tw-flex tw-flex-wrap tw-gap-y-4"
                                style={{ minHeight: '36px' }}
                                data-is-wrapping={isWrapping}
                                value={field.value || ''}
                                onValueChange={(value) => {
                                    field.onChange(value);
                                }}
                                disabled={readonly}
                            >
                                {options.map((option, idx) => {
                                    const isFocusTarget = field.value
                                        ? field.value === option.key
                                        : idx === 0;

                                    return (
                                        <div key={option.key} className="tw-flex tw-items-center">
                                            <RadioGroupItem
                                                value={option.key}
                                                id={`${fieldPath}-${option.key}`}
                                                disabled={option.disabled}
                                                className={className}
                                                onClick={handleUrlChange}
                                                ref={isFocusTarget ? focusButtonRef : undefined}
                                            />
                                            <label htmlFor={`${fieldPath}-${option.key}`} className={cn(`tw-m-0 tw-ml-2 lg-font-dynamic
                                             ${option.disabled || readonly ? '!tw-text-t-inactive' : 'tw-text-t-primary'}`)}>
                                                {translateOption(option.key, attribute)}
                                            </label>
                                        </div>
                                    );
                                })}
                            </RadioGroup>
                        }
                    />
                </Layout>

            );
        }

    }

};

export { BooleanComponent };
